import { ColumnDef } from "@tanstack/react-table";
import ActionButton from "Components/atoms/buttons/action-button";
import SyncButton from "Components/molecule/sync-button";
import AreYouSureModal from "Components/organisms/are-you-sure-modal";
import SearchEmergencyContact from "Components/organisms/forms/search-emergency-contact-form";
import DataTableTemplate from "Components/templates/data-table-template";
import withRouter from "Components/Common/withRouter";
import useDataTable from "hooks/useDataTable";
import useManual from "hooks/useManual"
import { EmergencyContactRow, IEmergencyContactQueryParams } from "models/emergency-contact";
import { SyncLogTypes } from "models/sync-log";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { HumanResourcesSliceState, setShowSyncModal } from "slices/human-resources/reducer";
import { syncDayforceData } from "slices/human-resources/thunk";
import useSyncProcess from "hooks/useSyncProcess";
import { EmergencyContactsSliceState, setEmergencyContacts, setEmergencyContactSearchFields } from "slices/human-resources/emergency-contacts/reducer";
import { getEmergencyContacts, getEmergencyContactSearchFields } from "slices/human-resources/emergency-contacts/thunk";
import { SyncLogSliceState } from "slices/sync-log/reducer";
import { getMostRecentSyncLog } from "slices/thunk";
import { PlusIcon, HelpIcon } from "Components/atoms/icons";

const selectHumanResources = createSelector(
    (state: any) => state.HumanResources,
    ({ showSyncModal, isSyncingData }: HumanResourcesSliceState) => ({
        showSyncModal, isSyncingData
    })
)

const selectSyncLog = createSelector(
    (state: any) => state.SyncLog,
    ({ syncLogs }: SyncLogSliceState) => ({
        syncLogs
    })
)

const selectEmergencyContacts = createSelector(
    (state: any) => state.EmergencyContacts,
    ({ emergencyContacts, divisions, departments, provinces }: EmergencyContactsSliceState) => ({
        emergencyContacts, divisions, departments, provinces
    })
)

const DEFAULT_SEARCH: IEmergencyContactQueryParams = {
    employeeName: '',
    contactName: '',
    division: undefined,
    department: undefined,
    province: undefined,
}

const TEXT_CELL_STYLE = {
    getCellStyle: () => ({
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    }),
}

const toTelHref = (phone: string) => `tel:${phone.replace(/[^\d+]/g, '')}`

const toOptions = (values: string[]) => [
    { value: '', label: 'All' },
    ...values.map(v => ({ value: v, label: v }))
]

const EmergencyContactsPage = () => {
    document.title = "Emergency Contacts | IbexIQ";
    const { handleOpenManual } = useManual("human-resources/emergency-contacts");
    const dispatch: any = useDispatch();
    const [query, setQuery] = useState<IEmergencyContactQueryParams>(DEFAULT_SEARCH);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const { showSyncModal } = useSelector(selectHumanResources);
    const { syncLogs } = useSelector(selectSyncLog)
    const { emergencyContacts, divisions, departments, provinces } = useSelector(selectEmergencyContacts)

    const fetchEmergencyContacts = async (q: IEmergencyContactQueryParams) => {
        setIsLoading(true)
        const rows = await getEmergencyContacts(q);
        if (rows) dispatch(setEmergencyContacts(rows))
        setIsLoading(false)
    }

    const handleChangeQuery = <K extends keyof IEmergencyContactQueryParams>(
        key: K,
        value: IEmergencyContactQueryParams[K]
    ) => {
        setQuery(prev => ({ ...prev, [key]: value }));
    };

    const handleSearch = () => fetchEmergencyContacts(query)

    const handleClearSearch = () => {
        const q = { ...DEFAULT_SEARCH }
        setQuery(q);
        fetchEmergencyContacts(q)
    }

    const initializePage = async () => {
        if (!syncLogs[SyncLogTypes.EMPLOYEE_DAYFORCE])
            dispatch(getMostRecentSyncLog(SyncLogTypes.EMPLOYEE_DAYFORCE))

        if (divisions.length === 0 && departments.length === 0 && provinces.length === 0) {
            const fields = await getEmergencyContactSearchFields()
            if (fields) dispatch(setEmergencyContactSearchFields(fields))
        }

        await fetchEmergencyContacts(query)
    }

    useEffect(() => {
        initializePage()
    }, [])

    // This just opens the are you sure modal.
    const handleOpenAreYouSureModal = () => {
        dispatch(setShowSyncModal({ show: true }))
    }

    const handleAddEmergencyContact = () => {
        window.open("https://www.dayforcehcm.com/mydayforce/login.aspx", "_blank")
    }

    const onModalClose = async () => {
        dispatch(setShowSyncModal({ show: false }))
    }

    const { isSyncing: isSyncingData, startSync } = useSyncProcess({
        module: SyncLogTypes.EMPLOYEE_DAYFORCE,
        start: syncDayforceData,
        onFinished: () => fetchEmergencyContacts(query),
    })

    const handleSyncData = async () => {
        dispatch(setShowSyncModal({ show: false }))
        await startSync()
    }

    const columns = React.useMemo<ColumnDef<EmergencyContactRow>[]>(() => [
        {
            header: "Legal Name",
            id: "legalName",
            accessorFn: (row) => row.legalName,
            meta: TEXT_CELL_STYLE,
            size: 150,
        },
        {
            header: "Phone Number",
            id: "phoneNumber",
            accessorFn: (row) => row.phoneNumber ?? undefined,
            meta: TEXT_CELL_STYLE,
            size: 110,
            cell: ({ row }) => row.original.phoneNumber
                ? <a
                    href={toTelHref(row.original.phoneNumber)}
                    style={{ color: "var(--bs-table-link-color)", whiteSpace: "nowrap" }}
                >
                    {row.original.phoneNumber}
                </a>
                : null
        },
        {
            header: "Emergency Contact Name",
            id: "contactName",
            accessorFn: (row) => row.contactName ?? undefined,
            meta: TEXT_CELL_STYLE,
            size: 150,
        },
        {
            header: "Emergency Contact Cell",
            id: "contactCell",
            accessorFn: (row) => row.contactCell ?? undefined,
            meta: TEXT_CELL_STYLE,
            size: 110,
        },
        {
            header: "Office Province",
            id: "officeProvince",
            accessorFn: (row) => row.officeProvince ?? undefined,
            meta: TEXT_CELL_STYLE,
            size: 100,
        },
        {
            header: "Division",
            id: "division",
            accessorFn: (row) => row.division ?? undefined,
            meta: TEXT_CELL_STYLE,
            size: 130,
        },
        {
            header: "Department",
            id: "department",
            accessorFn: (row) => row.department ?? undefined,
            meta: TEXT_CELL_STYLE,
            size: 130,
        },
    ], []);

    const table = useDataTable({
        columns,
        data: emergencyContacts,
        columnResizeMode: 'onEnd',
        enableColumnResizing: true
    })

    return <DataTableTemplate
        stickyFoot={false}
        title={"Human Resources"}
        breadcrumbItem={"Emergency Contact"}
        table={table}
        columns={columns}
        isLoading={isLoading}
        rowCount={emergencyContacts.length}
        actions={[
            <SyncButton
                id="emergencyContactsSyncBtn"
                isSyncing={isSyncingData}
                syncLog={syncLogs[SyncLogTypes.EMPLOYEE_DAYFORCE]}
                onClick={handleOpenAreYouSureModal}
            />,
            <ActionButton
                onClick={handleAddEmergencyContact}
            >
                Add <PlusIcon />
            </ActionButton>,
            <ActionButton
                onClick={handleOpenManual}
            >
                Help <HelpIcon />
            </ActionButton>
        ]}
        filters={<SearchEmergencyContact
            handleChangeQuery={handleChangeQuery}
            handleClearSearch={handleClearSearch}
            handleSearch={handleSearch}
            divisions={toOptions(divisions)}
            departments={toOptions(departments)}
            provinces={toOptions(provinces)}
            query={query}
            isSearching={isLoading}
        />}
        modal={<AreYouSureModal
            isOpen={showSyncModal}
            onModalClose={onModalClose}
            modalTitle={"Sync Emergency Contacts"}
            subTitle={"Sync Dayforce and IbexIQ employees"}
            handleAreYouSureAccept={handleSyncData}
            handleAreYouSureDecline={onModalClose}
            areYouSureacceptText={isSyncingData ? 'Syncing data...' : "Sync Data"}
            areYouSuredeclineText={"Cancel"} />}
    />
}

export default withRouter(EmergencyContactsPage)
